import type { CartItemForClient } from "@/lib/shared/types/interfaces";
import { getProductPrice, getTotal } from "@/lib/shared/utils/calculatePrice";
import type { DetectedGiftSet, GiftSet } from "./types";

type PoolEntry = {
  productId: string;
  variantId: string;
  unitPrice: number;
  remaining: number;
  lineKey: string;
};

function buildPool(items: CartItemForClient[], conversionRate: number): PoolEntry[] {
  return items.map((item) => ({
    productId: item.product.id,
    variantId: item.variant.id,
    unitPrice: getProductPrice(item.product, item.variant, conversionRate),
    remaining: item.quantity,
    lineKey: `${item.product.id}:${item.variant.id}`,
  }));
}

function bundleUnitPrice(giftSet: GiftSet, conversionRate: number): number {
  return Number((giftSet.price * conversionRate).toFixed(2));
}

function canAllocateOneBundle(pool: PoolEntry[], giftSet: GiftSet): boolean {
  const requiredQuantities = new Map<string, number>();
  const availableQuantities = new Map<string, number>();

  for (const { product } of giftSet.products) {
    requiredQuantities.set(product.id, (requiredQuantities.get(product.id) ?? 0) + 1);
  }

  for (const entry of pool) {
    availableQuantities.set(
      entry.productId,
      (availableQuantities.get(entry.productId) ?? 0) + entry.remaining,
    );
  }

  return [...requiredQuantities].every(
    ([productId, requiredQuantity]) =>
      (availableQuantities.get(productId) ?? 0) >= requiredQuantity,
  );
}

/** Allocates one already-validated bundle instance from `pool`. */
function allocateOneBundle(pool: PoolEntry[], giftSet: GiftSet, conversionRate: number) {
  const matches: { giftSetProductRowId: string; cartLineKey: string; quantity: number }[] = [];
  let memberSum = 0;

  for (const row of giftSet.products) {
    const productId = row.product.id;
    const entry = pool.find((p) => p.productId === productId && p.remaining > 0);
    if (!entry) {
      throw new Error(`Bundle allocation missing product ${productId} after availability check.`);
    }
    memberSum += entry.unitPrice;
    entry.remaining -= 1;
    matches.push({
      giftSetProductRowId: row.id,
      cartLineKey: entry.lineKey,
      quantity: 1,
    });
  }

  const bundlePrice = bundleUnitPrice(giftSet, conversionRate);
  const savings = Math.max(0, Number((memberSum - bundlePrice).toFixed(2)));
  const appliedPrice = Number((memberSum - savings).toFixed(2));

  return {
    savings,
    appliedPrice,
    originalTotal: Number(memberSum.toFixed(2)),
    matchedProducts: matches.map((m) => ({
      cartItemId: m.cartLineKey,
      giftSetProductId: m.giftSetProductRowId,
      quantity: m.quantity,
    })),
  };
}

/**
 * Greedy bundle detection: gift sets sorted by `priority` ascending consume cart quantities first.
 * Multiple complete bundles of the same set are applied repeatedly while stock allows.
 */
export function computeGiftSetBundleAdjustments(
  items: CartItemForClient[],
  conversionRate: number,
  giftSets: GiftSet[],
): { totalSavings: number; detections: DetectedGiftSet[] } {
  if (giftSets.length === 0 || items.length === 0) {
    return { totalSavings: 0, detections: [] };
  }

  const sorted = [...giftSets].sort((a, b) => a.priority - b.priority);
  const pool = buildPool(items, conversionRate);
  const detections: DetectedGiftSet[] = [];
  let totalSavings = 0;

  for (const giftSet of sorted) {
    if (giftSet.products.length === 0) continue;

    while (canAllocateOneBundle(pool, giftSet)) {
      const allocation = allocateOneBundle(pool, giftSet, conversionRate);

      totalSavings += allocation.savings;
      detections.push({
        giftSet,
        appliedPrice: allocation.appliedPrice,
        originalTotal: allocation.originalTotal,
        savings: allocation.savings,
        matchedProducts: allocation.matchedProducts,
      });
    }
  }

  return { totalSavings: Number(totalSavings.toFixed(2)), detections };
}

export function getCartTotalWithGiftSets(
  items: CartItemForClient[],
  conversionRate: number,
  giftSets: GiftSet[],
  shippingFee = 0,
): number {
  const raw = getTotal(items, conversionRate, shippingFee);
  const { totalSavings } = computeGiftSetBundleAdjustments(items, conversionRate, giftSets);
  return Number((raw - totalSavings).toFixed(2));
}

/** Smallest currency units for Stripe (`amount`), same rounding as payment routes. */
export function getCartTotalStripeSmallestUnits(
  items: CartItemForClient[],
  conversionRate: number,
  giftSets: GiftSet[],
  shippingFee = 0,
): number {
  return Math.round(getCartTotalWithGiftSets(items, conversionRate, giftSets, shippingFee) * 100);
}

export function detectGiftSetsFromCart(
  items: CartItemForClient[],
  conversionRate: number,
  giftSets: GiftSet[],
): DetectedGiftSet[] {
  return computeGiftSetBundleAdjustments(items, conversionRate, giftSets).detections;
}

/** One title per gift set id, preserving first-seen order (for cart UI). */
export function uniqueGiftSetDisplayTitlesFromDetections(detections: DetectedGiftSet[]): string[] {
  const seen = new Set<string>();
  const titles: string[] = [];
  for (const d of detections) {
    const id = d.giftSet.id;
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    titles.push(d.giftSet.displayTitle);
  }
  return titles;
}
